import { useState } from "react";

import { formatPhone } from "@smart/bridge-smokeball-basic";
import { useAsync } from "@smart/itops-hooks-dom";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";

import { GqlSubmissionSummary } from "../../types";

export const useSubmissionsWithMatter = ({
  submissions,
  loading,
  withMissingContactDetailsSet,
}: {
  submissions: GqlSubmissionSummary[];
  loading?: boolean;
  withMissingContactDetailsSet?: boolean;
}) => {
  const { matters, contacts } = useSmokeballApp();
  const [submissionsWithMatter, setSubmissionsWithMatter] =
    useState<GqlSubmissionSummary[]>(submissions);

  const isRecipientDetailsMissing = (submission: GqlSubmissionSummary) =>
    submission.values.recipient &&
    (!submission.values.recipient.email ||
      (!submission.values.recipient.cell &&
        !submission.values.recipient.phone));

  const setRecipientDetailsIfMissing = async (
    submission: GqlSubmissionSummary,
  ) => {
    if (
      submission.values.recipient?.email &&
      (submission.values.recipient.cell || submission.values.recipient.phone)
    )
      return submission;

    if (submission.values.recipient && submission.values.recipient.contactId) {
      const recipientContact = await contacts?.get(
        submission.values.recipient.contactId,
      );
      if (recipientContact) {
        return {
          ...submission,
          values: {
            ...submission.values,
            recipient: {
              ...submission.values.recipient,
              email:
                submission.values.recipient.email ||
                recipientContact.person?.email ||
                recipientContact.company?.email ||
                "",
              cell:
                submission.values.recipient.cell ||
                formatPhone(recipientContact.person?.cell) ||
                formatPhone(recipientContact.company?.phone) ||
                "",
              phone:
                submission.values.recipient.phone ||
                formatPhone(recipientContact.person?.phone) ||
                formatPhone(recipientContact.company?.phone) ||
                "",
            },
          },
        };
      }
    }

    return submission;
  };

  useAsync(async () => {
    if (loading || !submissions) return;

    setSubmissionsWithMatter(submissions);

    if (
      submissions.some(
        (submission) =>
          !submission.matter ||
          (withMissingContactDetailsSet &&
            isRecipientDetailsMissing(submission)),
      )
    ) {
      const withMatter = await Promise.all(
        submissions.map(async (submission) => {
          const updatedSubmission = withMissingContactDetailsSet
            ? await setRecipientDetailsIfMissing(submission)
            : submission;

          if (
            !updatedSubmission.values.matterId ||
            updatedSubmission.matter ||
            !matters
          )
            return updatedSubmission;

          let matter;
          try {
            matter = await matters.get(updatedSubmission.values.matterId);
          } catch (error) {
            console.error("Error getting matter by SDK");
          }

          return {
            ...updatedSubmission,
            matter: matter?.id
              ? { ...matter, __typename: "Matter" as const }
              : undefined,
          };
        }),
      );
      setSubmissionsWithMatter(withMatter);
    }
  }, [submissions, loading, withMissingContactDetailsSet]);

  return { submissionsWithMatter };
};
